import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CreateQuestionRequest } from "../../entry/server/game.ts";
import { gameRepo } from "../../repo/game-repo.ts";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { AppContext } from "../../context/app-context.tsx";
import QuestionFormFields, { emptyQuestionForm, isQuestionFormValid } from "./question-form-fields.tsx";

export default function EditQuestion() {
  const navigate = useNavigate();
  const { id } = useParams();
  const questionId = Number(id);
  const { setMessage } = useContext(AppContext);
  const [form, setForm] = useState(emptyQuestionForm);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!questionId) {
      setMessage({ type: "error", message: "شناسه سوال معتبر نیست." });
      navigate("/admin/questions");
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const response = await gameRepo.getQuestion(questionId);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت سوال ناموفق بود." });
          navigate("/admin/questions");
          return;
        }
        const data = response.data;
        setForm({
          question: data.question ?? "",
          option1: data.option1 ?? "",
          option2: data.option2 ?? "",
          option3: data.option3 ?? "",
          option4: data.option4 ?? "",
          correct_option: String(data.correct_option ?? ""),
        });
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
        navigate("/admin/questions");
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [questionId, navigate, setMessage]);

  const onChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isQuestionFormValid(form) || !questionId) {
      setMessage({ type: "error", message: "لطفاً همه فیلدها را کامل کنید." });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: CreateQuestionRequest = {
        question: form.question.trim(),
        option1: form.option1.trim(),
        option2: form.option2.trim(),
        option3: form.option3.trim(),
        option4: form.option4.trim(),
        correct_option: form.correct_option,
      };

      const response = await gameRepo.updateQuestion(questionId, payload);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "ویرایش سوال ناموفق بود." });
        return;
      }

      setMessage({ type: "success", message: "سوال با موفقیت ویرایش شد." });
      navigate("/admin/questions");
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="space-y-4">
        <AdminPageTitle title="ویرایش سوال" />
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">در حال بارگذاری...</div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <AdminPageTitle title="ویرایش سوال" />
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
          <QuestionFormFields form={form} onChange={onChange} />
          <div className="flex items-center gap-3 md:col-span-2">
            <button
              type="submit"
              disabled={!isQuestionFormValid(form) || isSubmitting}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "در حال ذخیره…" : "ذخیره تغییرات"}
            </button>
            <Link
              to="/admin/questions"
              className="inline-flex rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
            >
              انصراف
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
}
